"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import SubscribeButton from '@/components/SubscribeButton';
import { useSubscribedCompanies } from '@/context/SubscribedCompaniesContext';
import { TranslatableLabel } from '@/components/TranslatableText';
import styles from './page.module.css';

interface AboutCompanyCardProps {
  company: string;
  companyDescription: string;
  logo: string;
  location: string;
  logoColor: string;
  employeeNumber: string;
  lang: string;
  subscribersCount?: number;
  companySlug?: string;
}

export default function AboutCompanyCard({
  company,
  companyDescription,
  logo,
  location,
  logoColor,
  employeeNumber,
  lang,
  subscribersCount,
  companySlug,
}: AboutCompanyCardProps) {
  const words = companyDescription ? companyDescription.split(/\s+/) : [];
  const LIMIT = 80;
  const isLong = words.length > LIMIT;
  const [expanded, setExpanded] = useState(false);
  const [mounted, setMounted] = useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const { isSubscribed } = useSubscribedCompanies();
  const subscribed = isSubscribed(company);
  
  const displaySubscribersCount = subscribersCount !== undefined 
    ? (subscribed ? subscribersCount + 1 : subscribersCount) 
    : undefined;

  const displayText = isLong && !expanded
    ? words.slice(0, LIMIT).join(' ') + '...'
    : companyDescription;

  return (
    <div className={styles.aboutCompanyCard}>
      <div className={styles.aboutCompanyHeader}>
        <h2 className={styles.sectionTitle}><TranslatableLabel translationKey="About company" /></h2>
      </div>

      <div className={styles.aboutCompanyFollow}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          {mounted && (
            <SubscribeButton 
              company={{ name: company, logo: logo, location: location, logoColor: logoColor, id: companySlug }} 
              variant="follow"
              className={styles.followBtnSmall}
            />
          )}
          {mounted && displaySubscribersCount !== undefined && (
            <div className={styles.subscribersMobile}>
              <span>{displaySubscribersCount}</span> <TranslatableLabel translationKey="followers" />
            </div>
          )}
        </div>
      </div>

      <div className={styles.aboutCompanyContent}>
        {companyDescription && (
          <>
            <p className={styles.companyDescText}>{displayText}</p>
            {isLong && (
              <button
                className={styles.showMoreBtn}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}
      </div>

      <div className={styles.aboutCompanyRight}>
        <Link href={companySlug ? `/${lang}/companies/${companySlug}` : `/${lang}/companies`} className={styles.companyLogoLink}>
          <div className={styles.companyLogoSmall} style={{ backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {logo && logo.trim() !== '' ? (
              <img src={logo} alt={company} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} />
            ) : (
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.25rem'
              }}>
                {company ? company.charAt(0).toUpperCase() : '🏢'}
              </div>
            )}
          </div>
        </Link>
        <div className={styles.aboutCompanyInfoWrapper}>
          <Link href={companySlug ? `/${lang}/companies/${companySlug}` : `/${lang}/companies`} className={styles.companyNameLink}>
            <h4 className={styles.companyNameSmall}>{company}</h4>
          </Link>
          {employeeNumber && employeeNumber !== 'N/A' && <div className={styles.followersText}><span>{employeeNumber}</span> <TranslatableLabel translationKey="employees" /></div>}
          {mounted && displaySubscribersCount !== undefined && (
            <div className={styles.subscribersDesktop}>
              <span>{displaySubscribersCount}</span> <TranslatableLabel translationKey="followers" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
